import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type Adjunto, type RecorridoUnidad } from "@/lib/api";
import {
  ACABADOS_PISO, ARGENTINA, COLORES_PARED, direccionSol, estacionDe, ESTACIONES, etiquetaHora, fechaDeEscena, haciaFachada, nombreFoto360, posicionSol, resumenSol,
  RUMBOS, type AcabadoPiso, type Estacion,
} from "@/lib/recorrido";
import { nombreUnidad } from "@/lib/unidades";

export type ModoRecorrido = "foto" | "maqueta";

/**
 * Recorrido inmersivo de una unidad (sin JSX; Mati, 2026-11-01): la unidad por dentro, el sol según la época y la hora,
 * y las terminaciones para probar. El admin carga hacia dónde mira el frente del edificio (para que el sol sea el real).
 * Si la unidad tiene fotos 360° (fotorrealistas, de Blender), arranca en ellas, una por ambiente; el admin las sube.
 */
export function useRecorridoTab() {
  const { fid, membresia } = useContextoApp();
  const { id } = useParams();
  const navegar = useNavigate();
  const esAdmin = membresia.rol === "admin";
  const recorrido = useApiResource<RecorridoUnidad>(`/api/fideicomisos/${fid}/unidades/${Number(id)}/recorrido`);
  const datos = recorrido.data ?? null;

  const [estacion, setEstacion] = useState<Estacion>(() => estacionDe(new Date()));
  const [hora, setHora] = useState(16);
  const [piso, setPiso] = useState<AcabadoPiso>("roble");
  const [paredes, setParedes] = useState(COLORES_PARED[0]!.valor);

  const latitud = datos?.sitio.latitud ? Number(datos.sitio.latitud) : ARGENTINA.latitud;
  const longitud = datos?.sitio.longitud ? Number(datos.sitio.longitud) : ARGENTINA.longitud;
  const posicion = useMemo(() => posicionSol(fechaDeEscena(estacion, hora), latitud, longitud), [estacion, hora, latitud, longitud]);
  const fachada = datos ? haciaFachada(datos.sitio.frente_azimut, datos.frente) : 0;
  const sol = useMemo(() => direccionSol(posicion, fachada), [posicion, fachada]);

  const accionRumbo = useAsyncAction((grados: number) => api.patch<unknown>(`/api/fideicomisos/${fid}/terreno`, { frente_azimut: grados }));

  // Fotos 360°
  const fotos = datos?.fotos_360 ?? [];
  const [modoElegido, setModo] = useState<ModoRecorrido | null>(null);
  const modo: ModoRecorrido = fotos.length === 0 ? "maqueta" : (modoElegido ?? "foto");
  const [fotoElegida, setFoto] = useState<number | null>(null);
  const foto = fotos.find((f) => f.id === fotoElegida) ?? fotos[0] ?? null;
  const accionSubir = useAsyncAction((archivo: File) => {
    const f = new FormData();
    f.append("archivo", archivo);
    return api.upload<Adjunto>(`/api/fideicomisos/${fid}/unidades/${Number(id)}/fotos360/adjuntos`, f);
  });
  const accionBorrar = useAsyncAction((adjuntoId: number) => api.delete<void>(`/api/fideicomisos/${fid}/adjuntos/${adjuntoId}`).then(() => true));

  return {
    titulo: datos ? nombreUnidad(datos.unidad) : "Recorrido",
    datos,
    cargando: !datos && recorrido.loading,
    error: !datos && recorrido.error ? (recorrido.error.status === 422 ? recorrido.error.message : "No se pudo cargar el recorrido") : null,
    reintentar: recorrido.recargar,
    volver: () => navegar("/proyecto"),
    // Foto realista o maqueta para recorrer
    hayFotos: fotos.length > 0,
    modo,
    elegirModo: setModo,
    fotos: fotos.map((f) => ({ id: f.id, nombre: nombreFoto360(f.nombre) })),
    fotoId: foto?.id ?? null,
    elegirFoto: setFoto,
    urlFoto: foto ? `/api/fideicomisos/${fid}/unidades/${Number(id)}/fotos360/${foto.id}/archivo` : null,
    nombreFoto: foto ? nombreFoto360(foto.nombre) : "",
    // Subir y borrar fotos 360° (sólo el admin)
    archivosFotos: fotos,
    rutaDescarga: (a: Adjunto) => `/api/fideicomisos/${fid}/adjuntos/${a.id}/descarga`,
    subirFoto: async (archivo: File) => {
      accionBorrar.limpiarError();
      if (await accionSubir.run(archivo)) recorrido.recargar();
    },
    borrarFoto: async (a: Adjunto) => {
      accionSubir.limpiarError();
      if (await accionBorrar.run(a.id)) recorrido.recargar();
    },
    subiendoFoto: accionSubir.loading,
    errorFotos: accionSubir.error ?? accionBorrar.error,
    // Sol
    estaciones: ESTACIONES,
    estacion,
    elegirEstacion: (v: string) => setEstacion(v as Estacion),
    hora,
    elegirHora: setHora,
    formatoHora: etiquetaHora,
    resumenSol: resumenSol(estacion, hora, posicion),
    sol,
    alturaSol: posicion.altura,
    // Terminaciones
    acabadosPiso: ACABADOS_PISO,
    piso,
    elegirPiso: (v: string) => setPiso(v as AcabadoPiso),
    coloresPared: COLORES_PARED,
    paredes,
    elegirParedes: setParedes,
    // Hacia dónde mira el frente (sólo el admin)
    esAdmin,
    rumbos: RUMBOS.map((r) => ({ valor: String(r.valor), etiqueta: r.etiqueta })),
    rumbo: datos?.sitio.frente_azimut === null || datos?.sitio.frente_azimut === undefined ? undefined : String(datos.sitio.frente_azimut),
    sinRumbo: datos !== null && datos.sitio.frente_azimut === null,
    sinUbicacion: datos !== null && datos.sitio.latitud === null,
    elegirRumbo: async (v: string) => {
      if (await accionRumbo.run(Number(v))) recorrido.recargar();
    },
    guardandoRumbo: accionRumbo.loading,
    errorRumbo: accionRumbo.error,
  };
}
